import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function DailyTaskReport() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    // Form states
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [clientName, setClientName] = useState('');
    const [selectedClient, setSelectedClient] = useState(null); // stores the Client object if chosen from list
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [building, setBuilding] = useState('');
    const [taskType, setTaskType] = useState('התקנה');
    const [description, setDescription] = useState('');
    const [images, setImages] = useState([]); // array of { file, previewUrl }
    const [requiresInvoice, setRequiresInvoice] = useState(false);
    const [invoiceName, setInvoiceName] = useState('');

    // Autocomplete list states
    const [clientsList, setClientsList] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // UI States
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Get Auth Token
    const getToken = () => {
        return localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
    };

    // Load clients for autocomplete
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const token = getToken();
                const res = await axios.get(`${API_BASE}/api/clients`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setClientsList(res.data || []);
            } catch (err) {
                console.error('Error fetching clients:', err);
            }
        };
        fetchClients();
    }, []);

    // Filter autocomplete suggestions based on clientName input
    useEffect(() => {
        if (!clientName.trim()) {
            setFilteredClients([]);
            setSelectedClient(null);
            return;
        }

        const filtered = clientsList.filter(c => 
            c.name.toLowerCase().includes(clientName.toLowerCase())
        );
        setFilteredClients(filtered);

        // Check if there is an exact match in the database
        const exactMatch = clientsList.find(c => 
            c.name.trim().toLowerCase() === clientName.trim().toLowerCase()
        );
        if (exactMatch) {
            setSelectedClient(exactMatch);
        } else {
            setSelectedClient(null);
        }
    }, [clientName, clientsList]);

    // Handle suggestion click
    const handleSelectSuggestion = (client) => {
        setClientName(client.name);
        setSelectedClient(client);
        setShowSuggestions(false);
        // Clear new client fields as this is an existing client
        setPhone('');
        setAddress('');
        setBuilding('');
    };

    // Handle image file selection
    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (images.length + files.length > 5) {
            alert('ניתן להעלות עד 5 תמונות בלבד.');
            return;
        }

        const newImages = files.map(file => ({
            file,
            previewUrl: URL.createObjectURL(file)
        }));

        setImages(prev => [...prev, ...newImages]);
    };

    // Remove selected image
    const handleRemoveImage = (index) => {
        setImages(prev => {
            const copy = [...prev];
            // Revoke object URL to avoid memory leak
            URL.revokeObjectURL(copy[index].previewUrl);
            copy.splice(index, 1);
            return copy;
        });
    };

    // Form submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const token = getToken();
        if (!token) {
            setError('אינך מחובר. אנא התחבר מחדש.');
            setLoading(false);
            return;
        }

        if (!clientName.trim()) {
            setError('חובה למלא שם לקוח.');
            setLoading(false);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('clientName', clientName);
            formData.append('date', date);
            formData.append('taskType', taskType);
            formData.append('description', description);
            formData.append('requiresInvoice', requiresInvoice);

            if (requiresInvoice) {
                formData.append('invoiceName', invoiceName || clientName);
            }

            if (selectedClient) {
                formData.append('client', selectedClient._id);
            } else {
                // If it's a new client, pass additional fields
                formData.append('phone', phone);
                formData.append('address', address);
                formData.append('building', building);
            }

            // Append images
            images.forEach((imgObj) => {
                formData.append('images', imgObj.file);
            });

            await axios.post(`${API_BASE}/api/daily-tasks`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setSuccess(true);
            // Clear inputs
            setClientName('');
            setPhone('');
            setAddress('');
            setBuilding('');
            setDescription('');
            setImages([]);
            setRequiresInvoice(false);
            setInvoiceName('');

            // Scroll to top to see success msg
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // Redirect back after 2 seconds
            setTimeout(() => {
                navigate('/professionals');
            }, 2000);

        } catch (err) {
            console.error('Error submitting task:', err);
            setError(err.response?.data?.error || err.message || 'אירעה שגיאה בשמירת הדיווח.');
        } finally {
            setLoading(false);
        }
    };

    const isNewClient = !selectedClient && clientName.trim().length > 0;

    return (
        <div className="min-h-screen bg-slate-950 text-white py-12 px-4 md:px-8" dir="rtl">
            <div className="max-w-2xl mx-auto">
                
                {/* Back button */}
                <Link to="/professionals" className="inline-flex items-center gap-2 text-slate-400 hover:text-blue-400 mb-8 transition-colors font-bold">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    חזרה לאזור המקצועי
                </Link>

                {/* Form Card */}
                <div className="backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-white/10 shadow-2xl bg-white/5 relative overflow-hidden">
                    
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8 border-b border-white/10 pb-6">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-black tracking-tight">דיווח עבודה יומי</h1>
                            <p className="text-slate-400 text-xs mt-1 uppercase tracking-wider font-semibold">אלום נואטחה בע"מ</p>
                        </div>
                    </div>

                    {/* Notification Messages */}
                    {success && (
                        <div className="mb-6 p-4 rounded-2xl text-sm font-semibold text-center border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                            ✓ הדיווח נשמר ונשלח למערכת בהצלחה! מועבר חזרה...
                        </div>
                    )}

                    {error && (
                        <div className="mb-6 p-4 rounded-2xl text-sm font-semibold text-center border border-red-500/30 bg-red-500/10 text-red-300">
                            {error}
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        
                        {/* Date Input */}
                        <div>
                            <label className="block text-slate-400 text-xs font-bold mb-2 uppercase tracking-wide">תאריך ביצוע העבודה:</label>
                            <input 
                                type="date" 
                                value={date} 
                                onChange={e => setDate(e.target.value)}
                                className="w-full px-5 py-4 rounded-2xl text-white border border-white/10 outline-none transition-all bg-white/5 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                                required
                            />
                        </div>

                        {/* Client Autocomplete Selection */}
                        <div className="relative">
                            <label className="block text-slate-400 text-xs font-bold mb-2 uppercase tracking-wide">שם הלקוח / אתר:</label>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    value={clientName} 
                                    onChange={e => {
                                        setClientName(e.target.value);
                                        setShowSuggestions(true);
                                    }}
                                    onFocus={() => setShowSuggestions(true)}
                                    placeholder="הקלד שם לקוח לחיפוש או לקוח חדש..."
                                    className="w-full px-5 py-4 rounded-2xl text-white border border-white/10 outline-none transition-all bg-white/5 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                                    required
                                    autoComplete="off"
                                />
                                {selectedClient && (
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 bg-blue-500/20 border border-blue-500/40 text-blue-300 text-[10px] font-bold px-2 py-1 rounded-lg">
                                        לקוח קיים
                                    </span>
                                )}
                            </div>

                            {/* Dropdown Suggestions */}
                            {showSuggestions && filteredClients.length > 0 && (
                                <ul className="absolute z-20 w-full mt-2 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-h-60 overflow-y-auto overflow-x-hidden">
                                    {filteredClients.map((client) => (
                                        <li 
                                            key={client._id}
                                            onClick={() => handleSelectSuggestion(client)}
                                            className="px-5 py-3 hover:bg-white/5 cursor-pointer text-sm font-semibold transition-colors flex justify-between items-center border-b border-white/5 last:border-0"
                                        >
                                            <span>{client.name}</span>
                                            {client.address && <span className="text-xs text-slate-500 font-normal">{client.address}</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {/* Close suggestions listener */}
                            {showSuggestions && (
                                <div 
                                    className="fixed inset-0 z-10" 
                                    onClick={() => setShowSuggestions(false)}
                                />
                            )}
                        </div>

                        {/* Conditional New Client Fields */}
                        {isNewClient && (
                            <div className="p-6 rounded-2xl border border-blue-500/20 bg-blue-500/5 space-y-4 animate-fadeIn">
                                <h3 className="text-sm font-bold text-blue-400 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                    לקוח חדש במערכת - הזן פרטי קשר וכתובת:
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-slate-400 text-[10px] font-bold mb-1.5 uppercase">טלפון לקוח:</label>
                                        <input 
                                            type="tel"
                                            value={phone}
                                            onChange={e => setPhone(e.target.value)}
                                            placeholder="מספר טלפון ליצירת קשר"
                                            className="w-full px-4 py-3 text-sm rounded-xl text-white border border-white/10 outline-none bg-slate-900/50 focus:border-blue-500/40"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-slate-400 text-[10px] font-bold mb-1.5 uppercase">כתובת (עיר / רחוב):</label>
                                        <input 
                                            type="text"
                                            value={address}
                                            onChange={e => setAddress(e.target.value)}
                                            placeholder="לדוגמא: חיפה, רחוב הרצל"
                                            className="w-full px-4 py-3 text-sm rounded-xl text-white border border-white/10 outline-none bg-slate-900/50 focus:border-blue-500/40"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-bold mb-1.5 uppercase">מספר בניין / דירה / קומה:</label>
                                    <input 
                                        type="text"
                                        value={building}
                                        onChange={e => setBuilding(e.target.value)}
                                        placeholder="לדוגמא: בניין 12 דירה 4"
                                        className="w-full px-4 py-3 text-sm rounded-xl text-white border border-white/10 outline-none bg-slate-900/50 focus:border-blue-500/40"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Task Type Dropdown */}
                        <div>
                            <label className="block text-slate-400 text-xs font-bold mb-2 uppercase tracking-wide">סוג העבודה:</label>
                            <select 
                                value={taskType} 
                                onChange={e => setTaskType(e.target.value)}
                                className="w-full px-5 py-4 rounded-2xl text-white border border-white/10 outline-none bg-slate-900/80 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                            >
                                <option value="התקנה" className="bg-slate-950">התקנה (Installation)</option>
                                <option value="תיקון" className="bg-slate-950">תיקון (Repair)</option>
                                <option value="תיקון/פיקס" className="bg-slate-950">תיקון / פיקס (Fix)</option>
                                <option value="אחר" className="bg-slate-950">אחר (Other)</option>
                            </select>
                        </div>

                        {/* Work Details Textarea */}
                        <div>
                            <label className="block text-slate-400 text-xs font-bold mb-2 uppercase tracking-wide">פרטי העבודה שבוצעה:</label>
                            <textarea 
                                value={description} 
                                onChange={e => setDescription(e.target.value)}
                                rows={5}
                                placeholder="פרט כאן את העבודות שביצעת, מידות, כמויות, או כל מידע רלוונטי..."
                                className="w-full px-5 py-4 rounded-2xl text-white border border-white/10 outline-none bg-white/5 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 resize-y"
                                required
                            />
                        </div>

                        {/* Direct Invoice Logic */}
                        <div className="p-4 rounded-2xl border border-white/10 bg-white/5">
                            <label className="flex items-center gap-3 cursor-pointer group">
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        checked={requiresInvoice}
                                        onChange={(e) => setRequiresInvoice(e.target.checked)}
                                        className="sr-only"
                                    />
                                    <div className={`w-6 h-6 rounded-md border-2 transition-all duration-300 flex items-center justify-center ${requiresInvoice ? 'bg-amber-500 border-amber-500 shadow-md shadow-amber-500/20' : 'border-white/20 group-hover:border-white/40'}`}>
                                        {requiresInvoice && (
                                            <svg className="w-4 h-4 text-slate-950" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                                <span className="text-slate-300 text-sm font-bold group-hover:text-white transition-colors">העבודה דורשת הוצאת חשבונית ישירה?</span>
                            </label>

                            {requiresInvoice && (
                                <div className="mt-4 animate-fadeIn">
                                    <label className="block text-slate-400 text-[10px] font-bold mb-1.5 uppercase">שם הלקוח לחשבונית:</label>
                                    <input 
                                        type="text"
                                        value={invoiceName}
                                        onChange={e => setInvoiceName(e.target.value)}
                                        placeholder={clientName || "הזן שם מלא להוצאת חשבונית"}
                                        className="w-full px-4 py-3 text-sm rounded-xl text-white border border-white/10 outline-none bg-slate-900 focus:border-amber-500/50"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Photo Capture Section */}
                        <div>
                            <label className="block text-slate-400 text-xs font-bold mb-2 uppercase tracking-wide">תמונות מהאתר (עד 5):</label>
                            
                            <input 
                                type="file"
                                accept="image/*"
                                capture="environment"
                                multiple
                                ref={fileInputRef}
                                onChange={handleImageChange}
                                className="hidden"
                            />

                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                {images.map((img, index) => (
                                    <div key={index} className="relative group aspect-square rounded-2xl overflow-hidden border border-white/10 bg-slate-900">
                                        <img 
                                            src={img.previewUrl} 
                                            alt={`Preview ${index + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveImage(index)}
                                            className="absolute top-2 left-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold opacity-90 transition-colors shadow-md"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}

                                {images.length < 5 && (
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="w-full aspect-square border-2 border-dashed border-white/10 hover:border-blue-500/50 hover:bg-blue-500/5 rounded-2xl flex flex-col items-center justify-center text-slate-500 hover:text-blue-400 transition-all cursor-pointer bg-white/5"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="text-[10px] font-bold uppercase tracking-wider">צלם / העלה</span>
                                    </button>
                                )}
                            </div>
                            <p className="text-[10px] text-slate-500 mt-2">{images.length}/5 תמונות נבחרו</p>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-5 rounded-2xl font-black text-lg tracking-wide uppercase text-white transition-all shadow-xl flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ boxShadow: '0 8px 30px rgba(59, 130, 246, 0.3)' }}
                            >
                                {loading ? (
                                    <>
                                        <svg className="w-6 h-6 animate-spin" viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                        </svg>
                                        שולח דיווח...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                        שלח דיווח עבודה
                                    </>
                                )}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
            
            {/* Inline keyframe for fadeIn animation */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
}
